// Copyright (c) Docugami, Inc. All rights reserved.

import { IList, IListProps, List } from "@fluentui/react"
import styled from "styled-components"
import { useDrop } from "react-dnd"
import { DndItem, DropTypes } from "./DndTypes"
import { DndListDropTarget } from "./DndListDropTarget"
import { useEffect, useRef, Ref } from "react"

const ListRoot = styled.div`
    flex: 1;
    display: flex;
    width: 100%;
    flex-direction: column;
    overflow: auto;
`

const EndDropTarget = styled.div`
    flex: 1;
    min-height: 20px;
`;

interface DndListProps<T = any> extends IListProps<T> {
    dropTypes: DropTypes;
    rootRef?: Ref<HTMLDivElement>;
    onDndDrop?: (dndItem: DndItem, index: number, item?: T) => void;
    canDndDrop?: (dndItem: DndItem) => boolean;
    onRenderDropTargetPreview?: (dndItem: DndItem) => React.ReactNode;
    disableDropIndices?: (dndItem: DndItem) => number[];
    enableEndListDropZone?: boolean;
}

export function DndList<T = any>(props: DndListProps<T>): JSX.Element {
    const { dropTypes, items } = props;
    const listRef = useRef<IList | null>(null);

    const [{ item: dropItem }, endListRef] = useDrop(() => ({
        accept: dropTypes,
        collect: m => ({
            isOver: m.isOver(),
            item: m.getItem() as DndItem | undefined
        }),
        canDrop: (item, m) => {
            return m.isOver({ shallow: true }) && (!props.canDndDrop || props.canDndDrop(item as DndItem));
        },
        drop: (item, m) => {
            props.onDndDrop?.(item as DndItem, items?.length ?? 0);
        }
    }), [dropTypes, items]);

    useEffect(() => {
        listRef.current?.forceUpdate();
    }, [dropItem]);

    function showDropTargetAtIndex(index: number) {
        return !!dropItem &&
            !!props.onDndDrop &&
            !props.disableDropIndices?.(dropItem).includes(index) &&
            (!props.canDndDrop || props.canDndDrop(dropItem)) &&
            props.dropTypes.includes(dropItem.type);
    }

    function onRenderItem(item?: T, index?: number, isScrolling?: boolean): React.ReactNode {
        if (!item || index === undefined || !props.onRenderCell) {
            return null;
        }

        return (
            <>
                {props.onRenderCell(item, index, isScrolling)}
                <DndListDropTarget show={showDropTargetAtIndex(index + 1)} dropTypes={props.dropTypes} renderPreview={props.onRenderDropTargetPreview} onDrop={dndItem => props.onDndDrop?.(dndItem, index + 1, item)} />
            </>
        );
    }

    const StartListDropTarget = () => (
        <DndListDropTarget
            show={showDropTargetAtIndex(0)}
            dropTypes={props.dropTypes}
            renderPreview={props.onRenderDropTargetPreview}
            onDrop={dndItem => props.onDndDrop?.(dndItem, 0)} />
    )

    return (
        <ListRoot ref={props.rootRef} data-is-scrollable='true'>
            <StartListDropTarget />
            <List
                {...props}
                componentRef={listRef}
                onRenderCell={onRenderItem}
                onShouldVirtualize={p => props.onShouldVirtualize?.(p) ?? false} />
            {!!dropItem && props.enableEndListDropZone && <EndDropTarget ref={endListRef} />}
        </ListRoot>
    )
}
